import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionLoanMenu } from "@/components/Holizontal/ActionLoanMenu";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronDown, HandCoins } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";


type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type LoanRow = {
  id: number;
  loan_number?: string | number;

  fname?: string;
  sname?: string;
  phone_number?: string;

  BRANCH?: string;
  PRODUCT?: string;
  CREATEDBY?: string;

  USERNAME?: string;
  USERNAME2?: string;

  loan_amount?: number | string;
  created_at?: string;

  APP_FEES?: string; // "Not Paid"
};

type ApiResponse = {
  data: LoanRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;

  // backend may return these, we can still accept them
  branches?: Branch[];
  bdos?: Bdo[];
};

type MetaListResponse<T> = { data: T[] };

const PER_PAGE = 10;
const API_PATH = "/actions/loans/queued-disbursement";

function parseAmount(a: LoanRow["loan_amount"]) {
  if (a == null) return 0;
  if (typeof a === "number") return a;
  const n = Number(String(a).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function QueuedDisbursementPage() {
  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);
  const [branchId, setBranchId] = useState("");
  const [bdoId, setBdoId] = useState("");

  // loading + error
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false); // list loading
  const [error, setError] = useState<string | null>(null);

  // meta
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [bdoLoading, setBdoLoading] = useState(false);

  // list
  const [rows, setRows] = useState<LoanRow[]>([]);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  // ✅ scroll target (top of listing)
  const listTopRef = useRef<HTMLDivElement | null>(null);

  // ✅ pagination smooth-scroll after new rows rendered
  const [pendingScrollTop, setPendingScrollTop] = useState(false);
  useEffect(() => {
    if (!pendingScrollTop) return;
    if (loading) return;

    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      // or: window.scrollTo({ top: 0, behavior: "smooth" });
    });

    setPendingScrollTop(false);
  }, [pendingScrollTop, loading, rows.length]);

  // Load branches once (recommended)
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setMetaLoading(true);
        const res = await apiFetch<MetaListResponse<Branch>>("/actions/loans/meta/branches");
        if (!alive) return;
        setBranches(res.data ?? []);
      } catch {
        if (!alive) return;
        setBranches([]);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ When branch changes: reset BDO & load BDOs for that branch
  useEffect(() => {
    let alive = true;

    setBdoId("");
    setBdos([]);

    if (!branchId) return;

    (async () => {
      try {
        setBdoLoading(true);
        const res = await apiFetch<MetaListResponse<Bdo>>(`/actions/loans/meta/bdos?branch_id=${branchId}`);
        if (!alive) return;
        setBdos(res.data ?? []);
      } catch {
        if (!alive) return;
        setBdos([]);
      } finally {
        if (alive) setBdoLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  // reset paging snapshot when filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced, branchId, bdoId]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);

    // ✅ only send bdo_id if branch selected (prevents weird filtering)
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, branchId, bdoId, cursor, anchorId]);

  async function load(isBoot = false) {
    if (isBoot) setBootLoading(true);
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}?${queryString}`);

      setRows(json.data ?? []);

      // if backend still sends meta lists, accept them (but branches are loaded via /meta/branches)
      if ((json.branches ?? []).length) setBranches(json.branches ?? []);

      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
      if (isBoot) setBootLoading(false);
    }
  }

  // boot load
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on query changes
  useEffect(() => {
    if (bootLoading) return;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const totals = useMemo(() => {
    const totalAmount = rows.reduce((s, r) => s + parseAmount(r.loan_amount), 0);
    return { count: rows.length, totalAmount };
  }, [rows]);

  const resetAll = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setCursor(null);
    setAnchorId(null);
  };

  // ✅ scroll AFTER load finishes (like your better pager)
  const goPrev = () => {
    if (!prevCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(prevCursor);
  };

  const goNext = () => {
    if (!nextCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(nextCursor);
  };

  const customerName = (r: LoanRow) => `${r.fname ?? ""} ${r.sname ?? ""}`.replace(/\s+/g, " ").trim() || "—";
  const loanNumber = (r: LoanRow) => (r.loan_number ?? r.id).toString();

  // demo actions
  const onView = (r: LoanRow) => alert(`View loan: ${r.id}`);
  const onApprove = (r: LoanRow) => alert(`Approve loan: ${r.id}`);
  const onReject = (r: LoanRow) => alert(`Reject loan: ${r.id}`);

  const navigate = useNavigate();
  const location = useLocation();
  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const openCustomerProfile = (id: number | string) => {
    const selectedId = String(id);
    sessionStorage.setItem("selectedCustomer", selectedId);
    navigate("/customer-profile", {
      replace: location.pathname === "/customer-profile",
      state: { selectedCustomer: selectedId, nonce: Date.now() },
    });
  };

  const { t } = useTranslation();


  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <HandCoins className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">{t("queuedLoanDisbursement.title")}</h1>
              <p className="text-xs text-muted-foreground">Loans awaiting CO approval</p>
            </div>
          </div>
        </div>
      </header>

      <ActionLoanMenu />

      {/* ✅ Top loader bar (same style) */}
      {loading && (
        <div className="sticky top-0 z-10">
          <div className="h-1 w-full bg-muted overflow-hidden rounded">
            <motion.div
              className="h-full bg-primary"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Loan number / name / phone..."
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Branch</div>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={metaLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (metaLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">{metaLoading ? "Loading branches..." : "All Branches"}</option>
              {branches.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">BDO</div>
            <select
              value={bdoId}
              onChange={(e) => setBdoId(e.target.value)}
              disabled={!branchId || bdoLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (!branchId || bdoLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">
                {!branchId ? "Select Branch first" : bdoLoading ? "Loading BDOs..." : "All BDOs"}
              </option>
              {bdos.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="px-3 py-1 rounded-full bg-muted">
              Rows: <span className="font-semibold">{totals.count}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Total: <span className="font-semibold">{helper.formatMoneyKES(totals.totalAmount)}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Anchor: <span className="font-semibold">{anchorId ?? "-"}</span>
            </div>
            {loading && <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground">Loading…</div>}
          </div>

          <button
            onClick={resetAll}
            disabled={loading}
            className={cn(
              "h-10 px-3 rounded-md border border-border text-sm font-medium",
              loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Listing */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        <div ref={listTopRef} />

        {/* ✅ Overlay loader (same style) */}
        {loading && (
          <div className="absolute inset-0 z-10 rounded-xl bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <motion.span
                className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
              />
              Loading pending loans...
            </div>
          </div>
        )}

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              No pending loans.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.id}
                className="rounded-xl border border-border bg-background p-3 cursor-pointer hover:bg-muted/30 transition"
                onClick={() => openCustomerProfile(r.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{customerName(r)}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                      <span className="truncate">#{loanNumber(r)}</span>
                      <span>•</span>
                      <span className="truncate">{r.phone_number ?? "-"}</span>
                      <span>•</span>
                      <span className="truncate">{r.BRANCH ?? "-"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {r.PRODUCT ?? "-"} • Fees: {r.APP_FEES ?? "-"}
                    </div>
                  </div>

                 
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">App.Amount</div>
                    <div className="font-bold">{helper.formatMoneyKES(parseAmount(r.loan_amount))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Created By</div>
                    <div className="font-medium">{r.CREATEDBY ?? "-"}</div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Bottom pagination (mobile) */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">Customer Name</th>
                  <th className="px-4 py-3 font-semibold">Loan Info.</th>
                  <th className="px-4 py-3 font-semibold">App. Fees</th>
                  <th className="px-4 py-3 font-semibold">Created By</th>
                  
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      No pending loans.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>

                      <td className="px-4 py-3">
                        <div className="font-medium">{customerName(r)}</div>
                        <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="font-medium text-primary">{loanNumber(r)}</div>
                        <div className="text-xs text-muted-foreground">{r.PRODUCT ?? "-"}</div>
                        <div className="text-xs text-muted-foreground">{helper.compactDate(r.created_at)}</div>
                        <div className="font-semibold">{helper.formatMoneyKES(parseAmount(r.loan_amount))}</div>
                      </td>

                      <td className="px-4 py-3">{r.APP_FEES ?? "-"}</td>
                      <td className="px-4 py-3">{r.CREATEDBY ?? "-"}</td>

                      
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom pagination (desktop) */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
       
    </div>
  );
}